import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import { Base } from "@/atoms-react/text/Text.react";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { MandateService } from "@/features/company/types";
import { useAddress } from "@/features/user/address/hooks/use-address";
import { useDeleteAlias } from "@/features/user/hooks/use-delete-alias";
import { useUser } from "@/features/user/hooks/use-user";
import type { AddressFullType } from "@/features/user/types";
import { NaturalEntityDataType } from "@/features/utils/types";
import { AddressCard } from "@/molecules/address-card/index.react";
import { ModalAddAddress } from "@/molecules/modal-add-address/index.react";
import { ModalDeleteAlias } from "@/molecules/modal-delete-alias/index.react";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import type React from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { LayoutTitle } from "../../../_layout/index.react";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";

type AliasesAddressProps = { isOnboarding?: boolean } & React.ComponentPropsWithoutRef<"div">;

export const AliasesAddressApp = ({ isOnboarding = false, ...props }: AliasesAddressProps) => {
  const { t } = useTranslation(["pass"]);
  const { useUserProfile } = useUser();
  const { data: user } = useUserProfile();

  const { companyDetailQuery } = useCompany();
  const { leUuid } = useCompanyContext();
  const { data: companyDetails } = companyDetailQuery(leUuid);
  const addresses = leUuid ? companyDetails?.legalEntity.Address : user?.naturalPerson.Address;
  const olkypassLevel = user?.olkyPassLevel.olkypass_status.level;
  //const olkypassLevelUnderAudit = user?.olkyPassLevel.olkypass_status_under_audit.level;
  const [editAddress, setEditAddress] = useState<AddressFullType | null | undefined>(null);
  const { checkPermission } = useUserCheckAccess();
  const [modalAddAddress, setModalAddAddress] = useState<boolean>(false);
  const [deletedAddress, setDeletedAddress] = useState<AddressFullType | null>(null);
  const { createAddressMutation, editAddressMutation } = useAddress();
  const { mutateAsync: editAddressMutate } = editAddressMutation();
  const { mutateAsync: createAddress } = createAddressMutation();
  const { mutateAsync: deleteAlias, isPending: loading } = useDeleteAlias();
  const { auditPending, kycLevel } = useKYCContext();

  //Check if company is waiting consent

  const onEdit = (address: AddressFullType) => {
    setEditAddress(address);
    setModalAddAddress(true);
  };

  const onCreate = () => {
    setEditAddress(null);
    setModalAddAddress(true);
  };

  const enabled = leUuid
    ? !companyDetails?.legalEntity.UserValidation &&
      checkPermission(MandateService.ALIASES_MANAGEMENT, user, companyDetails)
    : kycLevel && kycLevel < 2 && !auditPending;

  const onDelete = async () => {
    if (!deletedAddress) {
      return;
    }
    if (!enabled) return;

    deleteAlias({
      ItemType: NaturalEntityDataType.AdresseDto,
      ItemUUID: deletedAddress.UUID || "",
      UUID: user?.naturalPerson?.UUID || ""
    }).then(async () => {
      setDeletedAddress(null);
    });
  };
  return (
    <Column {...props}>
      {!isOnboarding && (
        <LayoutTitle className="sm:mb-0">{t("aliasAddressPage.title")}</LayoutTitle>
      )}
      <div className="mb-4 flex w-full justify-start">
        <Button
          dataCy="button-add-address"
          className="mt-4 w-auto sm:mt-0"
          onClick={onCreate}
          icon={<PlusCircleIcon />}
          disabled={!!leUuid && !enabled}
        >
          {t("aliasAddressPage.button.add_address")}
        </Button>
      </div>

      <ModalAddAddress
        address={editAddress}
        open={modalAddAddress}
        onClose={() => setModalAddAddress(false)}
        onValidated={async (address) => {
          if (editAddress) {
            const updatedAddress: AddressFullType = {
              ...editAddress,
              Address1: address.address,
              ZipCode: address.zip,
              Country: address.country?.toString() || "",
              City: address.city,
              Tag: address.tag,
              isPrincipalAddress: address.isPrincipalAddress
            };
            await editAddressMutate({
              address: updatedAddress,
              leUuid
            });
            toast.success(t("aliasAddressPage.address_updated"));
          } else {
            await createAddress({ userUUID: user?.naturalPerson?.UUID || "", address })
              .then(() => toast.success(t("success.create")))
              .catch(() => toast.error(t("error.create")));
          }
          //we set it to undefined to trigger ModalAddAddress
          //re-render -> clear inputs after saving
          setEditAddress(undefined);
          setModalAddAddress(false);
        }}
      />
      <ModalDeleteAlias
        isOpen={!!deletedAddress}
        onClose={() => setDeletedAddress(null)}
        onValidated={onDelete}
        isConfirmLoading={loading}
        disabled={leUuid ? !enabled : deletedAddress?.isPrincipalAddress}
      >
        <Base>{t("aliasAddressPage.confirm_delete", { name: deletedAddress?.Tag })}</Base>
      </ModalDeleteAlias>
      <div className={"flex flex-wrap gap-4"}>
        {addresses &&
          addresses?.map((address) => (
            <AddressCard
              key={address.UUID}
              className="max-w-md flex-1"
              address={address}
              onDelete={() => setDeletedAddress(address)}
              onEdit={() => onEdit(address)}
              disabled={leUuid ? !enabled : !enabled && address.isPrincipalAddress}
              olkypassLevel={olkypassLevel}
            />
          ))}
      </div>
    </Column>
  );
};
